// eslint-disable typescript/no-unsafe-declaration-merging

import { EventEmitter } from 'events';
import HttpError from './HttpError';

class HttpClient extends EventEmitter {
  baseUrl?: string;
  private isLoggingEnabled?: boolean;

  constructor({ baseUrl, isLoggingEnabled }: HttpClientOptions = {}) {
    super();
    this.setBaseUrl(baseUrl);
    this.isLoggingEnabled = isLoggingEnabled;
  }

  setBaseUrl(baseUrl?: string) {
    let actualUrl = baseUrl;
    if (typeof actualUrl === 'string' && !actualUrl.endsWith('/')) {
      actualUrl += '/';
    }

    this.baseUrl = actualUrl;
  }

  async get<T = any>(input: RequestInfo, init?: RequestOptions): Promise<T> {
    return this.request(input, { method: 'GET', ...init });
  }

  async post<TData = any, TResult = any>(
    input: RequestInfo,
    data?: TData,
    init?: RequestOptions,
  ): Promise<TResult> {
    return this.request(input, {
      method: 'POST',
      body: data as any,
      ...init,
    });
  }

  async put<TData = any, TResult = any>(
    input: RequestInfo,
    data?: TData,
    init?: RequestOptions,
  ): Promise<TResult> {
    return this.request(input, {
      method: 'PUT',
      body: data as any,
      ...init,
    });
  }

  async delete(input: RequestInfo, init?: RequestOptions) {
    return this.request(input, { method: 'DELETE', ...init });
  }

  async request<T = any>(
    input: RequestInfo,
    init?: RequestOptions,
  ): Promise<T> {
    const startedAt = Date.now();

    const args = this.prepareFetchArguments(input, init);
    const response = await fetch(args.input, args.init);

    const data = await this.readResponseData(response);

    if (response.ok) {
      this.logRequest({
        method: init?.method,
        url: input,
        body: init?.body,
        response: data,
        startedAt,
      });
      return data;
    }

    const result = await this.handleResponseError<T>(
      response,
      data,
      input,
      init,
    );

    if (result instanceof Error) {
      this.logRequest({
        method: init?.method,
        url: input,
        body: init?.body,
        response: result.message,
        startedAt,
      });

      throw result;
    } else {
      return result;
    }
  }

  protected prepareFetchArguments(
    input: RequestInfo,
    init?: RequestOptions,
  ): FetchArguments {
    let transformedInput = input;

    if (typeof input === 'string') {
      const url = new URL(input, init?.baseUrl || this.baseUrl);

      if (init?.query) {
        Object.entries(init.query).forEach(([key, value]) => {
          if (value || value === 0 || value === false) {
            url.searchParams.set(key, value);
          }
        });
      }

      transformedInput = url.href;
    }

    const headers: Record<string, any> = {
      Accept: 'application/json',
      ...init?.headers,
    };

    const transformedInit = {
      ...init,
      headers,
    };

    if (init?.body) {
      if (init.body instanceof File) {
        // headers['Content-Type'] = init.body.type;
      } else {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
        // eslint-disable-next-line no-param-reassign
        transformedInit.body = JSON.stringify(init.body);
      }
    }

    return { input: transformedInput, init: transformedInit };
  }

  protected async readResponseData(response: Response) {
    const [contentType] = (response.headers.get('content-type') || '')
      .toLowerCase()
      .split(';');

    let text;

    try {
      text = await response.text();
    } catch (e) {
      if (response.status === 204) {
        return null;
      }

      throw new HttpError(`Can't read response body: ${e}`, response.status);
    }

    if (contentType !== 'application/json') {
      return text;
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new HttpError(`Can't parse "${text}": ${e}`, response.status);
    }

    return data;
  }

  protected async handleResponseError<T>(
    response: Response,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: any,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    input: RequestInfo,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    init?: RequestOptions,
  ): Promise<HttpError | T> {
    const errorData = data || response.statusText;
    return new HttpError(errorData, response.status, data?.stack);
  }

  protected prepareBodyData(data: any): BodyInit | undefined {
    if (!data) {
      return undefined;
    }

    if (data instanceof File) {
      return data;
    }

    return JSON.stringify(data);
  }

  protected logRequest({
    method,
    url,
    body,
    response,
    startedAt,
  }: { startedAt: number } & RequestLog) {
    this.emit('log', {
      method: method || 'GET',
      url,
      body,
      response,
      duration: Date.now() - startedAt,
    });
  }
}

export default HttpClient;

declare interface HttpClient {
  on(event: 'log', listener: (log: RequestLog) => void): this;
}

export interface RequestOptions extends RequestInit {
  baseUrl?: string;
  disableSubRequest?: boolean;
  query?: Record<string, any>;
}

export interface FetchArguments {
  init: RequestInit;
  input: RequestInfo;
}

export interface HttpClientOptions {
  baseUrl?: string;
  isLoggingEnabled?: boolean;
}

export interface RequestLog {
  body?: any;
  duration?: number;
  method?: string;
  response?: any;
  url: any;
}
